import CognitoAuthService from "src/components/login/CognitoAuthService"
import Clock from "src/general/Clock";
import StateConstants from "src/states/StateContstants"
import PersistentCookieHandler from "src/components/login/PersistentCookieHandler"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import navigationService from "src/navigation/NavigationService"
import PathRouter from "src/navigation/PathRouter"
import { clearUserEntitlements } from "src/utils/LocalDevAccess"

type CacheClearable = { clearCache: () => void }


class LoginHelper {
    loginDetails: any;
    cognitoAuthService: CognitoAuthService
    clock: Clock;
    stateHandler: EventAdaptingStateHandler;
    cookieHandler: PersistentCookieHandler;
    backendBaseApiUrl: string;
    private apiCacheClearer: CacheClearable | null = null;

    constructor(loginDetails: any, clock: Clock, stateHandler: EventAdaptingStateHandler, backendBaseApiUrl: string = '') {
        this.loginDetails = loginDetails
        this.clock = clock
        this.stateHandler = stateHandler
        this.cookieHandler = new PersistentCookieHandler(loginDetails.IS_HTTPS || false)
        this.cognitoAuthService = new CognitoAuthService({ loginDetails })
        this.backendBaseApiUrl = backendBaseApiUrl
    }

    setApiCacheClearer(clearer: CacheClearable) {
        this.apiCacheClearer = clearer
    }

    async getValidToken() {
        return await this.cognitoAuthService.getToken(this.backendBaseApiUrl)
    }

    async logout() {
        try {
            await this.cognitoAuthService.signOut()
        } catch (error) {
            console.error('LoginHelper: signOut error:', error)
        }
        this.setAuthenticationState(false)
        clearUserEntitlements(this.stateHandler)
        this.stateHandler.set(StateConstants.SAVED_VALUATIONS, null)
        this.stateHandler.set(StateConstants.PENDING_SAVE_VALUATION_PAYLOAD, null)
        this.apiCacheClearer?.clearCache()
        console.log('LoginHelper: Logout completed');
    }

    async signIn(email: string, password: string) {
        try {
            console.log('LoginHelper: Starting signIn process');
            const result = await this.cognitoAuthService.signIn(email, password)
            console.log('LoginHelper: signIn result:', result);

            if (result.success) {
                console.log('LoginHelper: SignIn successful, syncing auth state');
                await this.syncAuthenticationState();
                this.redirectToHome()
            }
            return result
        } catch (error) {
            console.error('LoginHelper: SignIn error:', error);
            throw error
        }
    }

    async signInWithoutRedirect(email: string, password: string) {
        try {
            const result = await this.cognitoAuthService.signIn(email, password)
            if (result.success) {
                await this.syncAuthenticationState()
            }
            return result
        } catch (error) {
            throw error
        }
    }

    async signUp(email: string, password: string) {
        try {
            return await this.cognitoAuthService.signUp(email, password)
        } catch (error) {
            throw error
        }
    }

    async confirmSignUp(email: string, code: string) {
        try {
            return await this.cognitoAuthService.confirmSignUp(email, code)
        } catch (error) {
            throw error
        }
    }

    async resendConfirmationCode(email: string) {
        try {
            return await this.cognitoAuthService.resendConfirmationCode(email)
        } catch (error) {
            throw error
        }
    }

    async forgotPassword(email: string) {
        try {
            return await this.cognitoAuthService.forgotPassword(email)
        } catch (error) {
            throw error
        }
    }

    async resetPassword(email: string, code: string, newPassword: string) {
        try {
            return await this.cognitoAuthService.resetPassword(email, code, newPassword)
        } catch (error) {
            throw error
        }
    }

    setAuthenticationState(isLoggedIn: boolean) {
        this.stateHandler.set(StateConstants.IS_LOGGED_IN, isLoggedIn)
    }

    async syncAuthenticationState() {
        try {
            const amplifyAuthState = await this.cognitoAuthService.isAuthenticated();
            const currentAppState = this.stateHandler.get(StateConstants.IS_LOGGED_IN);

            if (amplifyAuthState && !currentAppState) {
                this.setAuthenticationState(true);
            } else if (!amplifyAuthState && currentAppState) {
                this.setAuthenticationState(false);
            }
        } catch (error) {
            console.error('Sync: Error syncing authentication state:', error);
            this.setAuthenticationState(false);
        }
    }

    async isLoggedIn() {
        return Boolean(await this.cognitoAuthService.getToken(this.backendBaseApiUrl))
    }

    redirectToLoginSite() {
        if (this.stateHandler) {
            this.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true);
        }
    }

    redirectToHome() {
        let savedPageState = null;
        try {
            const cookieValue = this.cookieHandler.getCookie('saved_page_state');
            if (cookieValue) {
                savedPageState = JSON.parse(cookieValue);
            }
        } catch (error) {
            // Graceful fallback if cookie parsing fails
        }

        if (!savedPageState && this.stateHandler) {
            savedPageState = this.stateHandler.get(StateConstants.CURRENT_PAGE_STATE);
        }

        if (savedPageState && savedPageState.fullUrl) {
            const currentTime = this.clock.get_current_timestamp_in_millis();
            const timeDiff = currentTime - savedPageState.timestamp;
            const oneHourInMillis = 60 * 60 * 1000;

            if (timeDiff < oneHourInMillis) {
                this.cookieHandler.deleteCookie('saved_page_state');
                if (this.stateHandler) {
                    this.stateHandler.set(StateConstants.CURRENT_PAGE_STATE, null);
                }
                window.location.href = savedPageState.fullUrl;
                return;
            } else {
                this.cookieHandler.deleteCookie('saved_page_state');
            }
        }

        const url = window.location.href;
        const urlObj = new URL(url);
        const redirectParam = urlObj.searchParams.get('redirect');

        if (redirectParam) {
            if (redirectParam.startsWith('http://') || redirectParam.startsWith('https://')) {
                window.location.href = redirectParam;
            } else {
                navigationService.navigateTo(redirectParam);
            }
        } else {
            navigationService.navigateToHome();
        }
    }
}

export default LoginHelper
